import React from 'react'
import classnames from 'classnames'
import { kwhModelDeltaColorClass, lossWhenPositive, signedKwhDeltaColorClass } from '../cellColor'
import KwhCell from './KwhCell'
import NullDash from './NullDash'

export type SignedKwhCellRole = 'delta' | 'modelDelta' | 'loss'

export interface SignedKwhCellProps {
  value: number | null | undefined
  className?: string
  /** Semantic coloring; overridden by explicit valueClassName. */
  role?: SignedKwhCellRole
  /** Optional class for the numeric portion (the kWh suffix stays muted). */
  valueClassName?: string
}

const roleColorClass = (role: SignedKwhCellRole | undefined, value: number): string | undefined => {
  if (role === 'delta') return signedKwhDeltaColorClass(value)
  if (role === 'modelDelta') return kwhModelDeltaColorClass(value)
  if (role === 'loss') return lossWhenPositive(value)
  return undefined
}

/** Table-friendly signed kWh delta: +/- on the number, muted kWh suffix. Zero omits sign. */
export const SignedKwhCell: React.FC<SignedKwhCellProps> = ({
  value,
  className,
  role,
  valueClassName
}) => {
  if (value == null || Number.isNaN(value)) {
    return <NullDash className={className} />
  }
  if (value === 0) {
    return <KwhCell value={0} className={className} valueClassName={valueClassName} />
  }
  const absFormatted = Math.abs(Math.round(value)).toLocaleString()
  const numText = `${value < 0 ? '-' : '+'}${absFormatted}`
  const colorClass = classnames(roleColorClass(role, value), valueClassName)

  return (
    <span className={classnames('whitespace-nowrap tabular-nums', className)}>
      <span className={colorClass}>{numText}</span>
      <span className="ml-1 text-xs font-normal text-slate-400 dark:text-slate-400">kWh</span>
    </span>
  )
}

export default SignedKwhCell
